import type { Dispatch, SetStateAction } from 'react'
import { useEffect, useState } from 'react'
import { Button, Grid, GridItem, Link, TypographyV2 } from '@which/seatbelt'
import { CrossIcon } from '@which/seatbelt/src/components/Icons/Navigational'
import { getCookie } from '@which/shared'

import classNames from 'classnames'

import { Consent, hasCookieConsent } from '../../../../utils/hasCookieConsent'
import styles from './ConsentBanner.module.scss'

export const CONSENT_DISMISSED_COOKIE_NAME = 'just_in_time_consent_dismissed'

type setShowBannerType = Dispatch<SetStateAction<boolean | null>>
type setShowSuccessMessageType = Dispatch<SetStateAction<boolean>>

const setDismissedCookie = () => {
  const monthsToExpire = 6
  const days = 30
  const millisecondsPerDay = 86400 * 1000

  const date = new Date()
  const expireTime = date.getTime() + monthsToExpire * days * millisecondsPerDay
  date.setTime(expireTime)
  const expiryDate = date.toUTCString()

  document.cookie = `${CONSENT_DISMISSED_COOKIE_NAME}=1; expires=${expiryDate}; path=/`
}

const getUpdateConsentFunc = (setShowSuccessMessage: setShowSuccessMessageType) => {
  return () => {
    const setFunctionalCookie = () => window.OneTrust?.UpdateConsent('Category', 'C0002:1')
    setFunctionalCookie()
    setShowSuccessMessage(true)
    setDismissedCookie()
  }
}

type CloseButtonProps = {
  setShowBanner: setShowBannerType
}

const CloseButton = ({ setShowBanner }: CloseButtonProps) => {
  const closeBanner = () => {
    setDismissedCookie()
    setShowBanner(false)
  }

  const closeBannerOnKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      closeBanner()
    }
  }

  return (
    <button
      onClick={closeBanner}
      onKeyDown={closeBannerOnKeyDown}
      data-testid="closeButton"
      data-which-id="secondary-consent-banner-close"
      className={styles.R4UConsentBannerClose}
      aria-label="Close this consent banner"
    >
      <CrossIcon
        width={15}
        height={15}
        data-which-id="secondary-consent-banner-close"
        customColour="sb-colour-icon-decorative-muted"
      />
    </button>
  )
}

const ConsentRequestText = () => (
  <TypographyV2 textStyle="sb-text-body-default-regular" tag="p">
    By consenting to functional and performance cookies, Which? personalises your online experience
    and gathers analytics. For more information, please{' '}
    <Link
      href="https://www.which.co.uk/help/our-cookies-policy-your-cookies-preferences-aAuoo0H8Zif6"
      target="_blank"
      title="Link opens in a new window"
      aria-label="Cookie policy link opens in a new window"
    >
      click here
    </Link>
    .
  </TypographyV2>
)

const SuccessMessage = () => (
  <TypographyV2 textStyle="sb-text-body-default-regular" tag="span">
    You'll start seeing personalised recommendations over the coming days.
  </TypographyV2>
)

export const ConsentBanner = () => {
  const [showBanner, setShowBanner] = useState<boolean | null>(null)
  const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false)

  useEffect(() => {
    const noBannerDismiss = getCookie(document.cookie, CONSENT_DISMISSED_COOKIE_NAME) === ''
    const noFunctionalConsent = hasCookieConsent(Consent.FunctionalAndPerformance) === false

    setShowBanner(noBannerDismiss && noFunctionalConsent)
  }, [])

  if (!showBanner) {
    return null
  }

  return (
    <Grid
      className={classNames('w-page-wrapper', styles.R4UConsentBanner)}
      data-testid="R4UConsentBanner"
      data-which-id="secondary-consent-banner"
      includeGutters={false}
      tag="div"
    >
      <GridItem
        columnStart={{ large: 2 }}
        span={{ large: 10 }}
        className={styles.R4UConsentBannerBox}
      >
        <CloseButton setShowBanner={setShowBanner} />
        <Grid includeGutters={false} className={styles.R4UConsentBannerContent}>
          <GridItem
            span={{ small: 2, medium: 10, large: 10 }}
            className={styles.R4UConsentBannerText}
          >
            <TypographyV2
              textStyle="sb-text-heading-medium"
              tag="h3"
              className={styles.R4UConsentBannerTextTitle}
            >
              {showSuccessMessage ? 'Cookie preference updated' : 'Get better recommendations'}
            </TypographyV2>

            {showSuccessMessage ? <SuccessMessage /> : <ConsentRequestText />}
          </GridItem>

          {!showSuccessMessage && (
            <GridItem
              columnStart={{ small: 1, medium: 11, large: 11 }}
              span={{ small: 2, medium: 2, large: 2 }}
              className={styles.R4UConsentBannerButtonWrapper}
            >
              <Button
                appearance="primary"
                aria-label="Accept this consent banner"
                data-testid="acceptButton"
                data-which-id="secondary-consent-banner-accept"
                onClick={getUpdateConsentFunc(setShowSuccessMessage)}
                className={styles.R4UConsentBannerButton}
              >
                Yes — opt in
              </Button>
            </GridItem>
          )}
        </Grid>
      </GridItem>
    </Grid>
  )
}
